import type { SupportAdminTicketDetailDto } from '../../../../libs/api-client/src/generated/models';
import { supportEnumLabel, type Translate } from './support-enum-labels';

/**
 * Saying what someone else did to a ticket while an agent was working in it.
 *
 * WHETHER a ticket changed is not decided here — `stateToken` off the API is, and the reply
 * endpoint rules on it again at the instant of sending. This file only turns two versions
 * into words, because "this ticket changed" is a banner people learn to ignore and "Anna
 * added a message, assigned to anna" is one they read.
 *
 * Nothing here may become the authority on freshness. A second definition of "changed" on
 * this side would drift from the server's, and the version that let a duplicate answer
 * through would be the one nobody was testing.
 */

/**
 * Re-exported so a caller of {@link describeRemoteChanges} needs one import.
 *
 * Defined next to the enum labels rather than here: both modules take a translator as a
 * parameter, and two structurally identical declarations of it would drift.
 */
export type { Translate };

/**
 * Names each difference between the ticket on screen and the ticket on the server.
 *
 * The translator is a PARAMETER rather than a hook call, which keeps this a pure function of
 * (shown, latest, t) — the whole reason the phrasing lives here instead of in the component.
 *
 * @param shown - The ticket currently rendered.
 * @param latest - The ticket as the last poll found it.
 * @param t - Translator for the phrases, from `useLocale`.
 * @returns One phrase per change, empty when the two agree.
 */
export function describeRemoteChanges(
  shown: SupportAdminTicketDetailDto | undefined,
  latest: SupportAdminTicketDetailDto | undefined,
  t: Translate,
): string[] {
  if (!shown || !latest || shown.stateToken === latest.stateToken) return [];

  const changes: string[] = [];

  const newMessages = latest.messages.length - shown.messages.length;
  if (newMessages > 0) {
    const author =
      latest.messages[latest.messages.length - 1]?.authorName ??
      t('support.changes.someone');
    changes.push(
      newMessages === 1
        ? t('support.changes.messageAdded', { author })
        : t('support.changes.messagesAdded', { count: newMessages }),
    );
  }

  if (latest.assignee !== shown.assignee) {
    changes.push(
      latest.assignee
        ? t('support.changes.assignedTo', { assignee: latest.assignee })
        : t('support.changes.assignmentCleared'),
    );
  }
  // The values are translated, like every other enum this app shows — a banner that says "der
  // Status ist jetzt PENDING_CUSTOMER" mid-sentence is the one place the raw token reads worst.
  // The token is still one hover away on the queue badge and in the history panel, which is
  // where an operator cross-checks against the database anyway.
  if (latest.status !== shown.status) {
    changes.push(
      t('support.changes.statusNow', {
        value: supportEnumLabel(t, 'status', latest.status),
      }),
    );
  }
  if (latest.priority !== shown.priority) {
    changes.push(
      t('support.changes.priorityNow', {
        value: supportEnumLabel(t, 'priority', latest.priority),
      }),
    );
  }
  if (latest.category !== shown.category) {
    changes.push(
      t('support.changes.categoryNow', {
        value: supportEnumLabel(t, 'category', latest.category),
      }),
    );
  }

  // The review invitation, which is the one thing on this page a BACKGROUND job moves. Every
  // other itemised change above has a person behind it; this one fires on the worker three days
  // after the ticket was resolved, so an agent who happens to have the ticket open gets the
  // banner — and a held send explained only by "this ticket was updated" reads as a bug when
  // the actual answer is "we mailed your merchant a second ago".
  //
  // Ordered sent → scheduled → withdrawn, because `reviewAskSentAt` is the one that is never
  // cleared: a send both sets it and nulls `scheduledAt`, and reporting that as a withdrawal
  // would be exactly backwards.
  if (latest.reviewAsk.sentAt !== shown.reviewAsk.sentAt) {
    changes.push(t('support.changes.reviewAskSent'));
  } else if (latest.reviewAsk.scheduledAt !== shown.reviewAsk.scheduledAt) {
    changes.push(
      latest.reviewAsk.scheduledAt
        ? t('support.changes.reviewAskScheduled')
        : t('support.changes.reviewAskStopped'),
    );
  }

  // The token moved but none of the above did — a field the banner does not itemise. Say so
  // anyway: the send is held either way, and a held send with no stated reason reads as a bug.
  if (changes.length === 0) changes.push(t('support.changes.updated'));

  return changes;
}
